import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { MarketCategory, Instance } from '../../../app/services/api/orderManagementClient';
import CategoryItemRow from '../../../pages/Categories/components/CategoryItemRow';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import LoadingWheel from '../../../components/ui/LoadingWheel';

type Props = {
  list: MarketCategory[];
  isLoading: boolean;
  totalCount: number;
  instances: Instance[];
  onClear: () => void;
  onCreateNew: () => void;
};

const CategoriesList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  instances,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('categories');

  const getInstanceName = (companyId: number) => {
    const instance = instances.find(inst => inst.id === companyId);
    return instance ? instance.name : '-';
  };

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('categories')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('createNew')}
        onAddIsCreating={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('categories')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('categories')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <LoadingWheel />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('categoryName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('parentCategory')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('instanceName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('icon')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((category) => (
                    <CategoryItemRow
                      key={`${category.categoryId}-${category.companyId}`}
                      category={category}
                      parentCategories={list}
                      instanceName={getInstanceName(category.companyId)}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={4} 
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('noCategoriesFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default CategoriesList;
